import { reverse } from "@reactivated";

import {
    APIConcreteBundles,
    IConcreteBundle,
    IMattressMatchPreferenceSet,
    IMattressMatchResults,
    IProduct,
    IVerboseProductClass,
    MattressMatchResults,
    Product,
    Products,
    VerboseProductClass,
} from "../models/catalogue";
import {
    IProductAPIURL,
    IProductCategoryID,
    IProductID,
    ProductUUID,
    isoProductAPIURL,
    isoProductCategoryID,
    isoProductID,
    isoProductUUID,
} from "../models/nominals";
import { APIPrice, IAPIPrice } from "../models/prices";
import {
    IProductShippingMethod,
    ProductShippingMethods,
} from "../models/shipping";
import { check } from "../models/utils";
import { CSRF_HEADER, fetchJson, getCSRFToken } from "../utils/ajax";
import { PromiseMutex } from "../utils/mutex";

export const getProductURL = (productID: IProductID): IProductAPIURL => {
    const unwrappedProductID = isoProductID.unwrap(productID);
    return isoProductAPIURL.wrap(
        reverse("product-detail", { pk: unwrappedProductID }),
    );
};

export const getProductID = (productURL: IProductAPIURL): IProductID | null => {
    const groups = isoProductAPIURL.unwrap(productURL).match(/\/(\d+)\/$/);
    return groups ? isoProductID.wrap(parseInt(groups[1], 10)) : null;
};

export const getProductShippingMethods = async (
    productID: IProductID,
    postCode: string,
): Promise<ReadonlyArray<IProductShippingMethod>> => {
    const unwrappedProductID = isoProductID.unwrap(productID);
    const mutex = new PromiseMutex<ReadonlyArray<IProductShippingMethod>>(
        `product-shipping-methods-get-${unwrappedProductID}`,
    );
    let loading = mutex.getPromise();
    if (!loading) {
        const params = new URLSearchParams({ postcode: postCode });
        loading = fetchJson(
            `${reverse("product-shipping-methods", { pk: unwrappedProductID })}?${params}`,
        ).then((body) => {
            return check(ProductShippingMethods.decode(body));
        });
        mutex.setPromise(loading);
    }
    return loading;
};

export const getProduct = async (
    productURL: IProductAPIURL,
): Promise<IProduct> => {
    const unwrappedProductURL = isoProductAPIURL.unwrap(productURL);
    const mutex = new PromiseMutex<IProduct>(
        `product-get-${unwrappedProductURL}`,
    );
    let loading = mutex.getPromise();
    if (!loading) {
        loading = fetchJson(unwrappedProductURL).then((body) => {
            return check(Product.decode(body));
        });
        mutex.setPromise(loading);
    }
    return loading;
};

export const getProductPrice = async (
    productURL: IProductAPIURL,
    quantity: number,
): Promise<IAPIPrice> => {
    const unwrappedProductURL = isoProductAPIURL.unwrap(productURL);
    const mutex = new PromiseMutex<IAPIPrice>(
        `product-price-get-${unwrappedProductURL}-${quantity}`,
    );
    let loading = mutex.getPromise();
    if (!loading) {
        const params = new URLSearchParams({ quantity: String(quantity) });
        loading = fetchJson(`${unwrappedProductURL}price/?${params}`).then(
            (body) => {
                return check(APIPrice.decode(body));
            },
        );
        mutex.setPromise(loading);
    }
    return loading;
};

export const loadCategory = async (
    categoryID: IProductCategoryID,
): Promise<ReadonlyArray<IProduct>> => {
    const unwrappedCatID = isoProductCategoryID.unwrap(categoryID);
    const mutex = new PromiseMutex<ReadonlyArray<IProduct>>(
        `products-category-list-${unwrappedCatID}`,
    );
    let loading = mutex.getPromise();
    if (!loading) {
        loading = fetchJson(
            reverse("category-products", { pk: unwrappedCatID }),
        ).then((body) => {
            return check(Products.decode(body));
        });
        mutex.setPromise(loading);
    }
    return loading;
};

export const listConcreteBundles = async (
    productURL: IProductAPIURL,
): Promise<IConcreteBundle[]> => {
    const mutex = new PromiseMutex<IConcreteBundle[]>(
        `product-concrete-bundle-list-${productURL}`,
    );
    let loading = mutex.getPromise();
    if (!loading) {
        loading = fetchJson(`${productURL}concretebundles/`)
            .then((body) => {
                return check(APIConcreteBundles.decode(body));
            })
            .then((bundles) => {
                const loadingSuggestedProducts = bundles.map(
                    async (inputBundle) => {
                        // Load the details of each suggested product
                        const products = await Promise.all(
                            inputBundle.suggested_products.map((pID) => {
                                return getProduct(getProductURL(pID));
                            }),
                        );
                        // Filter out product which are out of stock
                        const availableProducts = products.filter(
                            (p) => p.availability.is_available_to_buy,
                        );
                        // Inject the remaining product details into the bundle data
                        const bundle: IConcreteBundle = {
                            ...inputBundle,
                            suggested_products: availableProducts,
                        };
                        return bundle;
                    },
                );
                return Promise.all(loadingSuggestedProducts);
            });
        mutex.setPromise(loading);
    }
    return loading;
};

// Batch state for getProductByUUID DataLoader pattern
interface PendingUUIDBatch {
    entries: Map<
        string,
        Array<{
            resolve: (value: IProduct | null) => void;
            reject: (error: unknown) => void;
        }>
    >;
}

let pendingBatch: PendingUUIDBatch | null = null;
let batchScheduled = false;

const executeBatch = async (): Promise<void> => {
    const batch = pendingBatch;
    pendingBatch = null;
    batchScheduled = false;

    if (!batch) return;

    const uuids = Array.from(batch.entries.keys());

    try {
        // Fetch concise products — use __in param for batch, single param for individual
        const params =
            uuids.length === 1
                ? `?core_product__uuid=${uuids[0]}`
                : `?core_product__uuid__in=${uuids.join(",")}`;
        const conciseProducts = await fetchJson<
            Array<{ id: number; [key: string]: unknown }>
        >(reverse("product-list") + params);

        // For each returned product, fetch verbose details
        const verboseResults = await Promise.allSettled(
            (conciseProducts || []).map((p) => {
                const url = getProductURL(isoProductID.wrap(p.id));
                return getProduct(url);
            }),
        );

        // Match verbose products back to requesters by UUID
        const productsByUUID = new Map<string, IProduct>();
        for (const result of verboseResults) {
            if (result.status === "fulfilled" && result.value.uuid) {
                productsByUUID.set(
                    isoProductUUID.unwrap(result.value.uuid),
                    result.value,
                );
            } else if (result.status === "rejected") {
                console.error(
                    "Failed to fetch product details in batch:",
                    result.reason,
                );
            }
        }

        // Resolve all promises
        for (const [uuid, resolvers] of batch.entries) {
            const product = productsByUUID.get(uuid) || null;
            for (const { resolve } of resolvers) {
                resolve(product);
            }
        }
    } catch (error) {
        // On error, reject all pending promises
        for (const resolvers of batch.entries.values()) {
            for (const { reject } of resolvers) {
                reject(error);
            }
        }
    }
};

export const getProductByUUID = async (
    productUUID: ProductUUID,
): Promise<IProduct | null> => {
    const UUIDRegex =
        /^[0-9a-f]{8}\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\b[0-9a-f]{12}$/;
    if (!productUUID || !isoProductUUID.unwrap(productUUID).match(UUIDRegex)) {
        return null;
    }
    const key = isoProductUUID.unwrap(productUUID);
    const mutex = new PromiseMutex<IProduct | null>(`product-get-uuid-${key}`);
    let loading = mutex.getPromise();
    if (loading) {
        return loading;
    }
    loading = new Promise<IProduct | null>((resolve, reject) => {
        if (!pendingBatch) {
            pendingBatch = { entries: new Map() };
        }
        if (!pendingBatch.entries.has(key)) {
            pendingBatch.entries.set(key, []);
        }
        pendingBatch.entries.get(key)!.push({ resolve, reject });
        if (!batchScheduled) {
            batchScheduled = true;
            queueMicrotask(() => void executeBatch());
        }
    });
    mutex.setPromise(loading);
    return loading;
};

export const getProductClass = async (
    productClassSlug: string,
): Promise<IVerboseProductClass> => {
    const mutex = new PromiseMutex<IVerboseProductClass>(
        `product-class-${productClassSlug}`,
    );
    let loading = mutex.getPromise();
    if (!loading) {
        loading = fetchJson(
            reverse("productclass-detail", { slug: productClassSlug }),
        ).then((body) => {
            return check(VerboseProductClass.decode(body));
        });
        mutex.setPromise(loading);
    }
    return loading;
};

export const submitMattressMatchPreferences = async (
    data: IMattressMatchPreferenceSet,
): Promise<IMattressMatchResults> => {
    const body = await fetchJson(reverse("mattress-match"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify(data),
    });
    return check(MattressMatchResults.decode(body));
};
